import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { createAIEvaluation, flattenExamQuestions, getAttemptScores, getEvaluationMark, getQuestionEntry } from '../utils/examScores';
import { gradeStarterSpreadsheet, hasDescriptiveResponse, hasStarterSpreadsheet } from '../utils/starterSpreadsheet';
import { spreadsheetToText } from '../components/editors/workbookModel';

// A disconnected service must never leave an exam trapped on a marking screen.
export function withEvaluationTimeout(promise, timeoutMs = 75000, message = 'Evaluation took too long. Please retry the pending answers.') {
    let timer;
    return Promise.race([
        promise,
        new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(message)), timeoutMs); }),
    ]).finally(() => clearTimeout(timer));
}

const markValue = value => Math.max(0, Number(value) || 0);
const normalized = value => String(value ?? '').trim().toLowerCase();
const selected = value => value !== undefined && value !== null && String(value).trim() !== '';
const sameSelection = (a, b) => selected(a) && selected(b) && String(a) === String(b);

export function gradeObjectiveQuestion(question, answer) {
    const correct = question.correctAnswer;
    switch (question.type) {
        case 'mcq': {
            const expected = Array.isArray(correct) ? correct.map(String).sort() : [];
            const actual = Array.isArray(answer) ? answer.map(String).sort() : [];
            return expected.length > 0 && expected.length === actual.length && expected.every((v, i) => v === actual[i]);
        }
        case 'dropdown': return sameSelection(answer, correct);
        case 'short-answer': return normalized(correct) !== '' && normalized(answer) === normalized(correct);
        case 'matching': {
            const keys = Object.keys(correct || {});
            return keys.length > 0 && keys.length === Object.keys(answer || {}).length && keys.every(key => sameSelection(answer?.[key], correct[key]));
        }
        case 'mcq-grid': return Boolean(question.rows?.length) && question.rows.every((_, index) => sameSelection(answer?.[index], correct?.[index]));
        default: return false;
    }
}

export function descriptiveAnswerForAI(answer) {
    const word = typeof answer === 'string' ? answer : answer?.wordContent;
    const sheet = answer?.excelData ? spreadsheetToText(answer.excelData) : '';
    return [word ? `Written response:\n${word}` : '', sheet ? `Spreadsheet working:\n${sheet}` : ''].filter(Boolean).join('\n\n');
}

// This engine is shared by first marking and approved revaluation. It receives the
// saved question version and nested case-study answers, and never changes either.
export async function evaluateExamAnswers({ questions, answers = {}, examId, strict = false, previousEvaluations = {}, retryPendingOnly = false, onProgress, timeoutMs = 75000, batchTimeoutMs = 240000 }) {
    const evaluations = {};
    const jobs = [];
    let score = 0;
    let autoGradedTotalMarks = 0;
    let descriptiveTotalMarks = 0;
    const saveEvaluation = (entry, evaluation) => {
        if (entry.subIndex === null) evaluations[entry.index] = evaluation;
        else {
            if (!evaluations[entry.index]) evaluations[entry.index] = {};
            evaluations[entry.index][entry.subIndex] = evaluation;
        }
    };
    for (const entry of flattenExamQuestions(questions)) {
        const q = entry.question;
        const marks = markValue(q.marks);
        const answer = getQuestionEntry(answers, entry.index, entry.subIndex, q.id);
        if (q.type !== 'descriptive') {
            autoGradedTotalMarks += marks;
            if (gradeObjectiveQuestion(q, answer)) score += marks;
            continue;
        }
        descriptiveTotalMarks += marks;
        const previous = getQuestionEntry(previousEvaluations, entry.index, entry.subIndex, q.id);
        if (retryPendingOnly && getEvaluationMark(previous) !== null) {
            saveEvaluation(entry, previous);
        } else if (hasStarterSpreadsheet(q) && q.starterSpreadsheet.gradingMode === 'cells') {
            const evaluation = gradeStarterSpreadsheet(q, answer);
            saveEvaluation(entry, { ...evaluation, maxMarks: marks, ...(evaluation.needsReview ? { mark: null, status: 'needs-review' } : { status: 'graded' }) });
        } else if (!hasDescriptiveResponse(q, answer)) {
            saveEvaluation(entry, { text: 'No answer provided.', mark: 0, maxMarks: marks, method: 'unanswered', status: 'graded' });
        } else {
            jobs.push({ entry, q, marks, answer });
        }
    }
    const deadline = Date.now() + batchTimeoutMs;
    let cursor = 0;
    let completed = 0;
    const report = () => onProgress?.({ completed, total: jobs.length });
    report();
    const evaluate = jobs.length ? httpsCallable(functions, 'evaluateDescriptiveAnswer', { timeout: timeoutMs }) : null;
    const worker = async () => {
        while (cursor < jobs.length) {
            const { entry, q, marks, answer } = jobs[cursor++];
            try {
                const remaining = deadline - Date.now();
                if (remaining <= 0) throw new Error('Marking time limit reached. Retry the remaining answers.');
                const modelSpreadsheet = q.suggestedAnswerExcel ? spreadsheetToText(q.suggestedAnswerExcel) : '';
                const response = await withEvaluationTimeout(evaluate({
                    studentAnswer: descriptiveAnswerForAI(answer),
                    suggestedAnswer: [q.suggestedAnswerContent || '', modelSpreadsheet ? `Model spreadsheet:\n${modelSpreadsheet}` : ''].filter(Boolean).join('\n\n'),
                    markingScheme: [q.markingSchemeContent || '', hasStarterSpreadsheet(q) ? `Award marks only for student work in these answer cells: ${q.starterSpreadsheet.answerRange}. Other cells are prefilled givens.` : ''].filter(Boolean).join('\n'),
                    marks,
                    ...(examId ? { examId } : {}),
                }), Math.min(timeoutMs, remaining));
                saveEvaluation(entry, createAIEvaluation(response.data, marks));
            } catch (error) {
                saveEvaluation(entry, { mark: null, maxMarks: marks, method: 'ai', status: 'failed', text: 'This answer is saved, but automatic marking did not finish. An administrator can retry evaluation.', error: String(error?.message || 'Evaluation failed').slice(0, 300) });
            }
            completed++;
            report();
        }
    };
    // Two in flight avoids sending an entire exam to the AI provider at once.
    await Promise.all(Array.from({ length: Math.min(2, jobs.length) }, worker));
    const { aiScore, pendingCount } = getAttemptScores({ evaluations });
    if (strict && pendingCount) throw new Error(`Revaluation could not confirm ${pendingCount} answer(s). Existing marks have been kept. Please retry or grade these answers manually.`);
    return {
        score: Number(score.toFixed(2)), autoGradedTotalMarks: Number(autoGradedTotalMarks.toFixed(2)),
        descriptiveTotalMarks: Number(descriptiveTotalMarks.toFixed(2)), evaluations, aiGradedScore: aiScore, pendingCount,
    };
}
